import React, { SetStateAction, useState } from "react";
import { EqualHeight } from "react-equal-height";
import { OfferSection } from "../OfferSection/OfferSection";
import { determineOfferAvailability } from "./determineOfferAvailability";
import { OfferAvailability } from "./OfferAvailability.type";
import { ILPGOffer } from "../../model/ILPGOffer";
import { IDeliverability } from "../../model/IDeliverability";
import { IOfferTabAnalytics, OfferTab, OfferType } from "./offerTab";
import { useProofPoints } from "../../context/ProofPointsContext";
import { useAppContext } from "../../context/AppContext";
import { LocationType } from "../../model/ILocationValidation";
import { OfferSelector } from "../OfferSelector/OfferSelector";
import { OfferDetailCard } from "../OfferDetailCard/OfferDetailCard";
import { OfferTypeUnavailable } from "../OfferTypeUnavailable/OfferTypeUnavailable";
import { UnableToSignup } from "../UnableToSignup/UnableToSignup";
import { AuthDealerScreen } from "../AuthDealerScreen/AuthDealerScreen";

function shouldSwitchTab(
  current: OfferAvailability,
  other: OfferAvailability,
  locationType?: LocationType
): boolean {
  const shouldSwitch =
    [
      OfferAvailability.OTHER_AVAILABLE,
      OfferAvailability.OTHER_DELIVER_ALTERNATE,
      // No offers on current tab but other tab has offers — switch so user sees offers immediately
      OfferAvailability.ERROR_OTHER_AVAILABLE,
    ].includes(current) ||
    // Current tab is undeliverable but other tab has something actionable — switch to it
    (current === OfferAvailability.UNDELIVERABLE &&
      (other === OfferAvailability.OFFERS_AVAILABLE ||
        other === OfferAvailability.DELIVER_ALTERNATE));

  // override and switch if location type is insitu
  if (!shouldSwitch && locationType === LocationType.INSITU) {
    return true;
  }

  return shouldSwitch;
}

export const Offers = ({
  offers,
  deliverability,
  locationType,
  loading,
  setAddressSelectorError,
  defaultOfferType,
  hasNaturalGas,
  naturalGasOriginAddressId,
}: {
  offers: ILPGOffer[];
  deliverability?: IDeliverability;
  locationType?: LocationType;
  loading: boolean;
  setAddressSelectorError: (error?: string) => void;
  defaultOfferType?: OfferType;
  hasNaturalGas?: boolean;
  naturalGasOriginAddressId?: string;
}) => {
  const [type, setType] = useState(defaultOfferType || OfferType.EXCHANGE);
  const [tabsTouched, setTabsTouched] = useState(false);
  const [analyticsSent, setAnalyticsSent] = useState<IOfferTabAnalytics>();
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const { proofPoints } = useProofPoints();
  const { context } = useAppContext();
  const { city: suburb, postcode } = context.lookupAddress?.qasAddress || {};
  const offerTab = OfferTab(type, suburb, postcode);
  const onChangeType: React.Dispatch<SetStateAction<OfferType>> = (
    offerType: SetStateAction<OfferType>
  ) => {
    setTabsTouched(true);
    setSelectedOffer(null);
    setType(offerType);
  };

  const otherType =
    type === OfferType.EXCHANGE ? OfferType.REFILL : OfferType.EXCHANGE;
  const offerAvailability = determineOfferAvailability(offers, deliverability);

  if (
    !tabsTouched &&
    shouldSwitchTab(
      offerAvailability[type],
      offerAvailability[otherType],
      locationType
    )
  ) {
    onChangeType(otherType);
  }

  const filteredOffers =
    type === OfferType.EXCHANGE
      ? offers.filter((offer) => !offer.isRefill)
      : offers.filter((offer) => offer.isRefill);

  // Auto-select the first offer; resets to null on tab switch so we fall back to first offer
  // of the new tab automatically without an extra render.
  // Also falls back to the first offer when the previously selected offer no longer exists
  // in the new address's offer list (e.g. Address A had 3 offers, Address B has only 2).

  const effectiveSelectedOffer =
    selectedOffer &&
    filteredOffers.some((o) => o.salesforceId === selectedOffer)
      ? selectedOffer
      : filteredOffers[0]?.salesforceId ?? null;

  // Fix for LPGDIGI-236: when user is shown alternate delivery details and then searches
  // a new address, the SUBURB in alternate details is updated (flicker) before offers for
  // new address is rendered.
  // OfferAvailability simplifies loader logic
  // - show price loader when OFFERS_AVAILABLE
  // - show full loader when showing anything else
  // - when no offers available (ie loading national offers), OfferAvailability is ERROR
  const showLoader =
    (loading &&
      offerAvailability[type] !== OfferAvailability.OFFERS_AVAILABLE) ||
    !proofPoints;

  const offerSelector =
    filteredOffers.length >= 1 ? (
      <OfferSelector
        offers={filteredOffers}
        selectedOffer={effectiveSelectedOffer}
        onSelectOffer={setSelectedOffer}
      />
    ) : undefined;

  const selectedOfferData = effectiveSelectedOffer
    ? filteredOffers.find((o) => o.salesforceId === effectiveSelectedOffer)
    : undefined;

  const offerDetailCard =
    selectedOfferData && proofPoints ? (
      <OfferDetailCard
        offer={selectedOfferData}
        loading={loading}
        setAddressSelectorError={setAddressSelectorError}
      />
    ) : undefined;

  const bothUndeliverable =
    offerAvailability[OfferType.EXCHANGE] === OfferAvailability.UNDELIVERABLE &&
    offerAvailability[OfferType.REFILL] === OfferAvailability.UNDELIVERABLE;

  const offerTypeUnavailable =
    !showLoader &&
    (offerAvailability[type] === OfferAvailability.UNDELIVERABLE ||
      offerAvailability[type] === OfferAvailability.OTHER_DELIVER_ALTERNATE ||
      (offers.length > 0 && filteredOffers.length === 0)) ? (
      <OfferTypeUnavailable
        type={type}
        bothUndeliverable={bothUndeliverable}
        offerTab={offerTab}
        analyticsSent={analyticsSent}
        setAnalyticsSent={setAnalyticsSent}
      />
    ) : undefined;

  const unableToSignup =
    !showLoader &&
    (offerAvailability[type] === OfferAvailability.OTHER_AVAILABLE ||
      (!!context.lookupAddress &&
        (offerAvailability[type] === OfferAvailability.ERROR ||
          offerAvailability[type] ===
            OfferAvailability.ERROR_OTHER_AVAILABLE))) ? (
      <UnableToSignup
        offerTab={offerTab}
        analyticsSent={analyticsSent}
        setAnalyticsSent={setAnalyticsSent}
        shouldFireAlert={true}
      />
    ) : undefined;

  // Hide "Select your bottle type" only when both types are undeliverable or there is an API
  // error — NOT when one type is undeliverable and the other is still available (UNDELIVERABLE
  // with other OFFERS_AVAILABLE or ERROR_OTHER_AVAILABLE), so the user can still switch tabs.
  const hideBottleTypeSection =
    !showLoader &&
    (bothUndeliverable ||
      (!!context.lookupAddress &&
        offerAvailability[type] === OfferAvailability.ERROR));

  const authDealerScreen =
    !showLoader &&
    offerAvailability[type] === OfferAvailability.DELIVER_ALTERNATE ? (
      <AuthDealerScreen
        deliverability={deliverability?.[type]}
        postcode={postcode}
        type={type}
        offerTab={offerTab}
        analyticsSent={analyticsSent}
        setAnalyticsSent={setAnalyticsSent}
      />
    ) : undefined;

  return (
    <EqualHeight>
      <OfferSection
        type={type}
        onChangeType={onChangeType}
        hasOffers={offers.length > 0}
        loading={loading}
        offerSelector={offerSelector}
        offerDetailCard={offerDetailCard}
        offerTypeUnavailable={offerTypeUnavailable}
        unableToSignup={unableToSignup}
        authDealerScreen={authDealerScreen}
        hideBottleTypeSection={hideBottleTypeSection}
        hasNaturalGas={hasNaturalGas}
        naturalGasOriginAddressId={naturalGasOriginAddressId}
        locationType={locationType}
        selectedOffer={selectedOfferData}
        offerTab={offerTab}
        analyticsSent={analyticsSent}
        setAnalyticsSent={setAnalyticsSent}
      />
    </EqualHeight>
  );
};
